import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  TextField,
  Button,
  Badge,
  DataTable,
  Text,
  Box,
  Divider,
  Banner,
  SkeletonBodyText,
} from '@shopify/polaris';
import { assetsApi } from '@/services';
import { orderNumber } from '@/utils/orderNumber';

const STATUS_BADGE: Record<string, { tone: 'success' | 'warning' | 'critical' | 'info' | undefined; label: string }> = {
  ready: { tone: 'success', label: 'Ready' },
  allocated: { tone: 'info', label: 'Allocated' },
  in_use: { tone: 'info', label: 'In Use' },
  qc_hold: { tone: 'warning', label: 'QC Hold' },
  repair: { tone: 'critical', label: 'Repair' },
  repair_complete: { tone: 'warning', label: 'Repair Complete' },
  at_clinic: { tone: 'info', label: 'At Clinic' },
  retired: { tone: 'critical', label: 'Retired' },
};

export default function SerialSearch() {
  const navigate = useNavigate();
  const [serial, setSerial] = useState('');
  const [searchSerial, setSearchSerial] = useState('');

  const { data: asset, isLoading, error } = useQuery({
    queryKey: ['asset-serial', searchSerial],
    queryFn: () => assetsApi.getBySerialOrGps(searchSerial),
    enabled: searchSerial.length > 0,
    retry: false,
  });

  const handleSearch = () => {
    if (serial.trim()) setSearchSerial(serial.trim());
  };

  const statusConfig = STATUS_BADGE[asset?.assetStatus || ''] || { tone: undefined, label: asset?.assetStatus || '' };

  const transactionRows = (asset?.transactions || []).map((tx: any) => [
    new Date(tx.createdAt).toLocaleString(),
    tx.transactionType,
    tx.previousStatus || '—',
    tx.newStatus,
    tx.rentalId ? (
      <Button key={tx.id} variant="plain" onClick={() => navigate(`/rentals/${tx.rentalId}`)}>
        View Rental
      </Button>
    ) : '—',
    tx.notes || '—',
    tx.performedBy || 'System',
  ]);

  const RENTAL_STATUS_BADGE: Record<string, { tone: 'success' | 'warning' | 'critical' | 'info' | undefined; label: string }> = {
    pending: { tone: 'info', label: 'Pending' },
    processing: { tone: 'info', label: 'Processing' },
    payment_failed: { tone: 'critical', label: 'Payment Failed' },
    ready_to_ship: { tone: 'warning', label: 'Ready to Ship' },
    shipped: { tone: 'info', label: 'Shipped' },
    in_use: { tone: 'success', label: 'In Use' },
    overdue: { tone: 'critical', label: 'Overdue' },
    extended: { tone: 'info', label: 'Extended' },
    returned: { tone: 'warning', label: 'Returned' },
    qc_hold: { tone: 'warning', label: 'QC Hold' },
    repair: { tone: 'critical', label: 'Repair' },
    complete: { tone: 'success', label: 'Complete' },
    cancelled: { tone: undefined, label: 'Cancelled' },
    refunded: { tone: undefined, label: 'Refunded' },
    fulfilled_by_clinic: { tone: 'success', label: 'Fulfilled by Clinic' },
    surgery_cancelled: { tone: undefined, label: 'Surgery Cancelled' },
    partial_refund: { tone: 'warning', label: 'Partial Refund' },
  };

  const rentalAssignments: any[] = asset?.rentalAssignments || [];

  const orderUsageRows = rentalAssignments.map((ra: any) => {
    const rentalItem = ra.rentalItem;
    const rental = rentalItem?.rental;
    const rentalStatusCfg = RENTAL_STATUS_BADGE[rental?.rentalStatus || ''] || { tone: undefined, label: rental?.rentalStatus || '—' };
    const tracking = rental?.shippingTrackingNumber || rental?.shipments?.[0]?.trackingNumber || '—';
    const dated = rental?.createdAt || rental?.rentalStartDate
      ? new Date(rental.createdAt ?? rental.rentalStartDate).toLocaleDateString()
      : '—';
    const weeklyRate = rentalItem?.weeklyRate != null
      ? `$${Number(rentalItem.weeklyRate).toFixed(2)}`
      : '—';

    return [
      rental?.id ? (
        <Button key={ra.id} variant="plain" onClick={() => navigate(`/rentals/${rental.id}`)}>
          {orderNumber(rental)}
        </Button>
      ) : '—',
      // TODO: distinguish rental vs sale order type when that data is available
      'Rental',
      rentalItem?.productName || '—',
      weeklyRate,
      <Badge key={`status-${ra.id}`} tone={rentalStatusCfg.tone}>{rentalStatusCfg.label}</Badge>,
      tracking,
      dated,
    ];
  });

  return (
    <Page
      title="Track Serial / GPS ID"
      backAction={{ content: 'Assets', onAction: () => navigate('/assets') }}
    >
      <BlockStack gap="400">
        <Card>
          <InlineStack gap="300" blockAlign="end">
            <Box minWidth="400px">
              <div onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}>
                <TextField
                  label="Serial Number or GPS ID"
                  value={serial}
                  onChange={setSerial}
                  autoComplete="off"
                  placeholder="Enter serial number or GPS tracker ID..."
                />
              </div>
            </Box>
            <Button variant="primary" onClick={handleSearch} loading={isLoading}>
              Search
            </Button>
          </InlineStack>
        </Card>

        {error && searchSerial && (
          <Banner tone="warning">
            <p>No asset found matching "{searchSerial}"</p>
          </Banner>
        )}

        {isLoading && (
          <Card><SkeletonBodyText lines={6} /></Card>
        )}

        {asset && (
          <>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text as="h2" variant="headingMd">Asset Details</Text>
                  <Badge tone={statusConfig.tone}>{statusConfig.label}</Badge>
                </InlineStack>
                <Divider />
                <BlockStack gap="200">
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Serial Number</Text>
                    <Text as="span" fontWeight="semibold">{asset.serialNumber}</Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">GPS ID</Text>
                    <Text as="span">{asset.gpsId ?? '—'}</Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Product</Text>
                    <Text as="span">{asset.productName}</Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">SKU</Text>
                    <Text as="span">{asset.productSku}</Text>
                  </InlineStack>
                  {asset.currentRentalId && (
                    <InlineStack align="space-between">
                      <Text as="span" tone="subdued">Current Rental</Text>
                      <Button variant="plain" onClick={() => navigate(`/rentals/${asset.currentRentalId}`)}>
                        View Rental
                      </Button>
                    </InlineStack>
                  )}
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Registered</Text>
                    <Text as="span">{new Date(asset.createdAt).toLocaleDateString()}</Text>
                  </InlineStack>
                </BlockStack>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Order Usage</Text>
                {orderUsageRows.length === 0 ? (
                  <Box padding="400">
                    <Text as="p" tone="subdued" alignment="center">No order history for this serial</Text>
                  </Box>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                    headings={['Order #', 'Order Type', 'Product', 'Weekly Rate', 'Status', 'Tracking', 'Dated']}
                    rows={orderUsageRows}
                  />
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Transaction History</Text>
                {transactionRows.length === 0 ? (
                  <Box padding="400">
                    <Text as="p" tone="subdued" alignment="center">No transaction history</Text>
                  </Box>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                    headings={['Date', 'Type', 'From', 'To', 'Rental', 'Notes', 'By']}
                    rows={transactionRows}
                  />
                )}
              </BlockStack>
            </Card>
          </>
        )}
      </BlockStack>
    </Page>
  );
}
